import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api, { goToLogin } from '../api';
import makeLogger from '../logger';

const log = makeLogger('auth');
const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [entitlements, setEntitlements] = useState(null);
  const [productId, setProductId] = useState(null);
  const [productAccess, setProductAccess] = useState(true);
  const [tokenExpiresAt, setTokenExpiresAt] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    log.debug('loading session via /me');
    try {
      const { data } = await api.get('/me');
      setUser(data.user);
      setEntitlements(data.entitlements);
      setProductId(data.productId);
      setProductAccess(data.productAccess !== false);
      setTokenExpiresAt(data.tokenExpiresAt);
      log.info('session loaded', {
        user: data.user && data.user.email,
        roles: (data.user && data.user.roles) || [],
        milientOrgId: data.user && data.user.milient_org_id,
        hasEntitlements: Boolean(data.entitlements),
        entitlementError: data.entitlementError || null,
        tokenExpiresAt: data.tokenExpiresAt ? new Date(data.tokenExpiresAt).toISOString() : null
      });
    } catch (err) {
      const status = err.response && err.response.status;
      log.warn(`session not available (status ${status || 'ERR'}); treating as logged out`);
      setUser(null);
      setEntitlements(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Explicitly exchange the refresh token for a fresh access token (the "Request new token" button).
  // Updates tokenExpiresAt so any view bound to it (e.g. the dashboard Access-token box) re-renders.
  const refreshAccessToken = useCallback(async () => {
    log.info('manual token refresh: POST /token/refresh');
    const { data } = await api.post('/token/refresh');
    setTokenExpiresAt(data.tokenExpiresAt);
    log.info('manual token refresh ok', { secondsLeft: data.secondsLeft });
    return data.tokenExpiresAt;
  }, []);

  // Optional milientOrgId logs directly into that org (Case 1); omit for the plain flow (Case 2).
  const login = (milientOrgId) => {
    log.info('login: redirecting to BFF /api/login', { organizationScoped: Boolean(milientOrgId) });
    goToLogin(milientOrgId);
  };

  const logout = async () => {
    log.info('logout: clearing session');
    try {
      const { data } = await api.post('/logout');
      setUser(null);
      setEntitlements(null);
      if (data.logoutUrl) {
        log.info('logout: redirecting to gateway RP-initiated logout');
        window.location.href = data.logoutUrl;
        return;
      }
    } catch (err) {
      log.warn('logout request failed; clearing local state anyway', err.message);
    }
    setUser(null);
    window.location.href = '/login';
  };

  const canonicalPermissions = entitlements?.entitlements?.canonical_permissions || [];
  // Layer B: product-specific permissions, grouped by module, e.g. { finance: ['invoice:read'], ... }.
  // Keyed in the gateway by user_id#organization_id#product_id (synced from the product).
  const productPermissions = entitlements?.entitlements?.product_permissions || {};
  const hasPermission = (perm) => canonicalPermissions.includes(perm);

  const value = {
    user,
    entitlements,
    productId,
    productAccess,
    tokenExpiresAt,
    loading,
    isAuthenticated: !!user,
    canonicalPermissions,
    productPermissions,
    hasPermission,
    refresh,
    refreshAccessToken,
    login,
    logout
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
